"use client";
import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Link from "next/link";

const WORDS = ["observes", "understands", "protects", "optimizes"];

// Data structure for our new premium feeds
const INDUSTRIAL_FEEDS = [
  { 
    id: 'crane', 
    title: 'PORT-CRANE SURVEILLANCE', 
    type: 'vimeo', 
    srcDesktop: 'https://player.vimeo.com/video/1189515532?background=1&badge=0&autopause=0&player_id=0&app_id=58479&autoPlay=1&muted=1&loop=1',
    srcMobile: 'https://player.vimeo.com/video/1189515506?background=1&autoPlay=1&muted=1&loop=1&autopause=0&title=0&byline=0&portrait=0',
    label: 'CAM-09 (ZONE-B CRANE)', 
    target: 'HUMAN_WORKER', 
    status: 'COMPLIANT' 
  },
  { 
    id: 'worker1', 
    title: 'WORKER SAFETY ZONE A', 
    type: 'local', 
    srcDesktop: '/assets/video/worker0-8.mp4',
    srcMobile: '/assets/video/worker0-8.mp4',
    label: 'CAM-12 (ASSEMBLY LINE)', 
    target: 'WORKER_ASSEMBLY', 
    status: 'WARNING: PROXIMITY' 
  },
  { 
    id: 'worker2', 
    title: 'WORKER SAFETY ZONE B', 
    type: 'local', 
    srcDesktop: '/assets/video/worker2-10.mp4',
    srcMobile: '/assets/video/worker2-10.mp4',
    label: 'CAM-14 (LOADING DOCK)', 
    target: 'WORKER_LOADING', 
    status: 'COMPLIANT' 
  }
];

const AGRICULTURE_FEEDS = [
  { 
    id: 'pig', 
    title: 'BREEDER COMPLEX NORTH', 
    type: 'local', 
    srcDesktop: '/assets/video/pig_crop.mp4',
    srcMobile: '/assets/video/pig_crop_5_7.mp4',
    label: 'CAM-04 (PIGGERY NORTH)', 
    target: '#0492-LIVESTOCK', 
    behavior: 'ACTIVE / FEEDING' 
  },
  { 
    id: 'cow1', 
    title: 'CATTLE MONITORING (PEN A)', 
    type: 'local', 
    srcDesktop: '/assets/video/cow(ok).mp4',
    srcMobile: '/assets/video/cow(ok).mp4',
    label: 'CAM-07 (CATTLE PEN A)', 
    target: '#1128-CATTLE', 
    behavior: 'RESTING' 
  },
  { 
    id: 'cow2', 
    title: 'GRAZING SECTOR B', 
    type: 'local', 
    srcDesktop: '/assets/video/cow2(ok).mp4',
    srcMobile: '/assets/video/cow2(ok).mp4',
    label: 'CAM-08 (FIELD B)', 
    target: '#0899-CATTLE', 
    behavior: 'GRAZING / MOVING' 
  }
];

export default function Hero({ lang = 'vn', setLang }: { lang?: 'vn' | 'en', setLang?: (lang: 'vn' | 'en') => void }) {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(WORDS[0].length);
  const [reverse, setReverse] = useState(false);
  const [blink, setBlink] = useState(true);
  
  const [activeVertical, setActiveVertical] = useState<"industrial" | "agriculture">("agriculture");
  const [activeIndFeed, setActiveIndFeed] = useState(0);
  const [activeAgFeed, setActiveAgFeed] = useState(0);

  
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [loadSecondary, setLoadSecondary] = useState(false);

  const currentIndFeed = INDUSTRIAL_FEEDS[activeIndFeed];
  const currentAgFeed = AGRICULTURE_FEEDS[activeAgFeed];

  // Typewriter effect
  useEffect(() => {
    if (subIndex === WORDS[index].length + 1 && !reverse) {
      const timeout = setTimeout(() => setReverse(true), 2000); // Hold full word
      return () => clearTimeout(timeout);
    }

    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % WORDS.length);
      return;
    }

    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, reverse ? 40 : 80); // Erase is faster than type

    return () => clearTimeout(timeout);
  }, [subIndex, reverse, index]);

  // Cursor blink
  useEffect(() => {
    const interval = setInterval(() => {
      setBlink((prev) => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Performance Optimization: Mount checks & idle loading
  useEffect(() => {
    setIsMounted(true);
    const checkMobile = () => setIsMobile(window.innerWidth < 991);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    // Idle load the inactive vertical's videos after 2.5s
    const timeout = setTimeout(() => setLoadSecondary(true), 2500);
    
    return () => {
      window.removeEventListener("resize", checkMobile);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <>
<div className="section-hero v2" style={{ position: "relative" }}>
 <div className="div-block-92" style={{ zIndex: 10 }}>
  <Navbar lang={lang} setLang={setLang} />
 </div>
 
 <div className="bg-video-overlay"></div>
 
 <div className="container-small z-index-high" style={{ zIndex: 5 }}>
  <div className="hero_content-parent" data-w-id="9995cf1e-a933-33bc-53c0-d39518c04de1" >
    
   {/* Interactive Premium Industry Selector & Language Toggle */}
   <div className="vertical-selector-container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "28px", zIndex: 10, position: "relative" }}>
     <div style={{ display: "flex", gap: "12px" }}>
       <button 
         onClick={() => setActiveVertical("industrial")}
         className={`selector-btn ${activeVertical === "industrial" ? "active-industrial" : ""}`}
       >
         <span className="selector-icon">🏭</span> {lang === 'vn' ? 'GIÁM SÁT CÔNG NGHIỆP' : 'INDUSTRIAL SAFETY FEED'}
       </button>
       <button 
         onClick={() => setActiveVertical("agriculture")}
         className={`selector-btn ${activeVertical === "agriculture" ? "active-agriculture" : ""}`}
       >
         <span className="selector-icon">🌾</span> {lang === 'vn' ? 'NÔNG NGHIỆP THÔNG MINH' : 'SMART AGRICULTURE FEED'}
       </button>
     </div>

   </div>

   <h1 className="h1-white" style={{ textShadow: "0 4px 20px rgba(0,0,0,0.85)", lineHeight: "1.2" }}>
    {lang === 'vn' ? 'AI ' : 'AI that '}
    <span className="type-text" style={{ color: activeVertical === "industrial" ? "#60a5fa" : "#34d399", borderRight: `3px solid ${activeVertical === "industrial" ? "#60a5fa" : "#34d399"}`, paddingRight: "4px", minWidth: "180px", display: "inline-block", transition: "color 0.4s ease" }}>
     {lang === 'vn' ? 'thấu hiểu' : WORDS[index].substring(0, subIndex)}
    </span>
    <br/>
    {lang === 'vn' ? 'thế giới thực' : 'the physical world'}
   </h1>
   
   <p className="p1 white-colour" style={{ marginTop: "18px", marginBottom: "28px", maxWidth: "620px", opacity: 0.95, textShadow: "0 2px 12px rgba(0,0,0,0.9)", lineHeight: "1.6", fontSize: "16px" }}>
     {activeVertical === "industrial"
       ? (lang === 'vn' ? 'Triển khai hệ thống giám sát video thông minh B2B liên tục theo thời gian thực tại nhà máy của bạn. Tự động hóa kiểm tra tuân thủ bảo hộ lao động (PPE), bảo vệ các khu vực nguy hiểm và đảm bảo thực thi SOP ngay tại biên.' : "Deploy continuous, real-time B2B video intelligence across your factory floor. Automate PPE compliance audits, safeguard restricted hazard zones, and enforce SOP execution instantly at the edge.") 
       : (lang === 'vn' ? 'Mở khóa công nghệ viễn trắc cấp độ thú y cho hoạt động chăn nuôi thông minh. Giám sát tư thế ăn của heo và bò, theo dõi bất thường theo thời gian thực và ngăn chặn các đợt bùng phát dịch bệnh 3 ngày trước khi có triệu chứng.' : "Unlock vet-grade telemetry on your smart livestock operations. Monitor swine and cattle feeding postures, track real-time activity anomalies, and prevent devastating herd outbreaks 3 days before symptoms surface.")}
   </p>
   
   <div className="spacer-1rem"></div>
   
   <a className="btn v2 w-button" href="mailto:contact@keeneyeai.site" style={{ 
     background: activeVertical === "industrial" ? "linear-gradient(135deg, #3b82f6, #1d4ed8)" : "linear-gradient(135deg, #10b981, #047857)", 
     border: "none", 
     boxShadow: activeVertical === "industrial" ? "0 4px 20px rgba(59, 130, 246, 0.45)" : "0 4px 20px rgba(16, 185, 129, 0.45)",
     transition: "all 0.4s ease"
   }}>
     {lang === 'vn' ? 'Đặt lịch Demo ngay' : 'Book a Live Demo'}
   </a>

   {/* Feed Switcher Controls */}
   <div className="feed-switcher-container" style={{ marginTop: "32px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", zIndex: 10, position: "relative" }}>
     <span style={{ fontSize: "11px", fontWeight: "bold", color: "rgba(255,255,255,0.6)", letterSpacing: "1px", marginRight: "4px" }}>LIVE CAMS:</span>
     {(activeVertical === "industrial" ? INDUSTRIAL_FEEDS : AGRICULTURE_FEEDS).map((feed, idx) => {
       const isActive = (activeVertical === "industrial" ? activeIndFeed : activeAgFeed) === idx;
       const color = activeVertical === "industrial" ? "#3b82f6" : "#10b981";
       const lightColor = activeVertical === "industrial" ? "#60a5fa" : "#34d399";
       return (
         <button 
           key={feed.id}
           onClick={() => activeVertical === "industrial" ? setActiveIndFeed(idx) : setActiveAgFeed(idx)}
           style={{
             padding: "6px 12px",
             borderRadius: "16px",
             fontSize: "10px",
             fontWeight: "bold",
             letterSpacing: "0.5px",
             background: isActive ? `rgba(${activeVertical === "industrial" ? "59, 130, 246" : "16, 185, 129"}, 0.2)` : "rgba(255,255,255,0.05)",
             border: `1px solid ${isActive ? color : "rgba(255,255,255,0.1)"}`,
             color: isActive ? lightColor : "#888",
             cursor: "pointer",
             transition: "all 0.3s ease",
           }}
         >
           {feed.label.split(' ')[0]} {/* Renders "CAM-09" etc */}
         </button>
       );
     })}
   </div>

  </div>
 </div>

  {/* Edge AI Overlays */}
  {/* Smart Agriculture Overlay Card */}
  <div className="edge-ai-overlay desktop-only" style={{ 
    left: "auto", 
    right: "8%", 
    top: "28%", 
    opacity: activeVertical === "agriculture" ? 1 : 0, 
    transform: activeVertical === "agriculture" ? "translateX(0)" : "translateX(20px)",
    pointerEvents: "none", 
    transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)" 
  }}>
    <div className="edge-ai-box">
      <div className="edge-ai-header">
        <span className="edge-ai-dot"></span>
        LIVE FEED: {currentAgFeed.label}
      </div>
      <div className="edge-ai-metrics">
        <div>TARGET ID: {currentAgFeed.target}</div>
        <div>BEHAVIOR: {currentAgFeed.behavior}</div>
        <div>HEALTH: 98.4% (EXCELLENT)</div>
        <div>BCS SCORE: 3.2 (VET-STANDARD)</div>
      </div>
    </div>
  </div>

  {/* Industrial Safety Overlay Card */}
  <div className="edge-ai-overlay-safety desktop-only" style={{ 
    right: "auto", 
    left: "8%", 
    bottom: "22%", 
    opacity: activeVertical === "industrial" ? 1 : 0, 
    transform: activeVertical === "industrial" ? "translateX(0)" : "translateX(-20px)",
    pointerEvents: "none", 
    transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)" 
  }}>
    <div className="edge-ai-box-safety">
      <div className="edge-ai-header-safety">
        <span className="edge-ai-dot-safety"></span>
        LIVE FEED: {currentIndFeed.label}
      </div>
      <div className="edge-ai-metrics-safety">
        <div>TARGET: {currentIndFeed.target}</div>
        <div>PPE SAFETY STATUS: {currentIndFeed.status}</div>
        <div>HARD HAT: ON (100% CONF)</div>
        <div>SAFETY HARNESS: DETECTED</div>
      </div>
    </div>
  </div>

  {/* Dual Feed Video Background - Desktop Full bleed with smooth cross-fade */}
  <div className="desktop-hero-bg-vid w-embed" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, overflow: "hidden", display: "block" }}>
   
   {/* Industrial Video backgrounds */}
   {INDUSTRIAL_FEEDS.map((feed, idx) => {
     const isActive = activeVertical === "industrial" && activeIndFeed === idx;
     const shouldLoad = isMounted && !isMobile && (activeVertical === "industrial" || loadSecondary);
     if (!shouldLoad && !isActive) return null;

     return (
       <div key={feed.id} className="hero-video-bg-fullscreen" style={{ 
         opacity: isActive ? 1 : 0, 
         zIndex: isActive ? 2 : 1,
         transition: "opacity 0.8s ease-in-out",
         position: "absolute",
         inset: 0,
         width: "100%",
         height: "100%"
       }}>
         {shouldLoad && feed.type === 'vimeo' ? (
           <iframe 
             allow="autoPlay; fullscreen; picture-in-picture" 
             allowFullScreen 
             frameBorder="0" 
             src={feed.srcDesktop} 
             className="hero-iframe-fullscreen"
           />
         ) : shouldLoad && feed.type === 'local' ? (
           <video autoPlay loop muted playsInline className="hero-video-fullscreen">
             <source src={feed.srcDesktop.replace('.mp4', '.webm')} type="video/webm" />
             <source src={feed.srcDesktop} type="video/mp4" />
           </video>
         ) : null}
         <div className="feed-label-fullscreen" style={{ borderColor: "#3b82f6", color: "#60a5fa", background: "rgba(59, 130, 246, 0.2)" }}>
           LIVE BROADCAST: {feed.title}
         </div>
       </div>
     );
   })}
   
   {/* Agriculture Video backgrounds */}
   {AGRICULTURE_FEEDS.map((feed, idx) => {
     const isActive = activeVertical === "agriculture" && activeAgFeed === idx;
     const shouldLoad = isMounted && !isMobile && (activeVertical === "agriculture" || loadSecondary);
     if (!shouldLoad && !isActive) return null;

     return (
       <div key={feed.id} className="hero-video-bg-fullscreen" style={{ 
         opacity: isActive ? 1 : 0, 
         zIndex: isActive ? 2 : 1,
         transition: "opacity 0.8s ease-in-out",
         position: "absolute",
         inset: 0,
         width: "100%",
         height: "100%"
       }}>
         {shouldLoad && (
           <video autoPlay loop muted playsInline className="hero-video-fullscreen">
             <source src={feed.srcDesktop.replace('.mp4', '.webm')} type="video/webm" />
             <source src={feed.srcDesktop} type="video/mp4" />
           </video>
         )}
         <div className="feed-label-fullscreen" style={{ borderColor: "#10b981", color: "#34d399", background: "rgba(16, 185, 129, 0.2)" }}>
           LIVE DIAGNOSTICS: {feed.title}
         </div>
       </div>
     );
   })}
   
   <style dangerouslySetInnerHTML={{__html: `
     .selector-btn {
       padding: 10px 20px;
       border-radius: 24px;
       font-size: 11px;
       font-weight: 800;
       letter-spacing: 1px;
       cursor: pointer;
       transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
       background: rgba(255, 255, 255, 0.04);
       border: 1px solid rgba(255, 255, 255, 0.08);
       color: #888;
       backdrop-filter: blur(10px);
       display: flex;
       align-items: center;
       gap: 8px;
       box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
     }
     
     .selector-btn:hover {
       background: rgba(255, 255, 255, 0.08);
       border-color: rgba(255, 255, 255, 0.2);
       color: #fff;
       transform: translateY(-2px);
     }
     
     .selector-btn.active-industrial {
       background: rgba(59, 130, 246, 0.15) !important;
       border: 1px solid #3b82f6 !important;
       color: #60a5fa !important;
       box-shadow: 0 0 25px rgba(59, 130, 246, 0.4) !important;
     }

     .selector-btn.active-agriculture {
       background: rgba(16, 185, 129, 0.15) !important;
       border: 1px solid #10b981 !important;
       color: #34d399 !important;
       box-shadow: 0 0 25px rgba(16, 185, 129, 0.4) !important;
     }

     .selector-icon {
       font-size: 13px;
     }

     .bg-video-overlay {
       position: absolute;
       inset: 0;
       width: 100%;
       height: 100%;
       background: linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.4) 100%) !important;
       z-index: 1;
       pointer-events: none;
     }
     
     .hero-iframe-fullscreen {
       position: absolute;
       top: 50%;
       left: 50%;
       width: 120vw;
       height: 67.5vw; /* 16:9 ratio */
       min-height: 100vh;
       min-width: 100%;
       transform: translate(-50%, -50%);
       pointer-events: none;
       filter: brightness(0.75) contrast(1.05) saturate(0.95);
     }
     
     .hero-video-fullscreen {
       width: 100%;
       height: 100%;
       object-fit: cover;
       filter: brightness(0.75) contrast(1.05);
     }
     
     .feed-label-fullscreen {
       position: absolute;
       top: 24px;
       right: 24px;
       border: 1px solid;
       padding: 8px 16px;
       border-radius: 6px;
       font-family: monospace;
       font-size: 11px;
       font-weight: bold;
       letter-spacing: 1px;
       z-index: 5;
       backdrop-filter: blur(8px);
       box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
       text-shadow: 0 1px 2px rgba(0,0,0,0.5);
     }
     
     .edge-ai-overlay {
       position: absolute;
       z-index: 3;
       pointer-events: none;
       font-family: monospace;
       animation: floatOverlay 4s ease-in-out infinite alternate;
     }
     .edge-ai-box {
       border: 2px solid #10b981;
       background: rgba(16, 185, 129, 0.15);
       box-shadow: 0 0 20px rgba(16, 185, 129, 0.4);
       padding: 10px 14px;
       border-radius: 8px;
       color: #34d399;
       backdrop-filter: blur(4px);
     }
     .edge-ai-dot {
       display: inline-block;
       width: 8px;
       height: 8px;
       background-color: #ef4444;
       border-radius: 50%;
       margin-right: 6px;
       animation: flash 1s infinite alternate;
     }
     .edge-ai-header {
       font-size: 10px;
       font-weight: bold;
       letter-spacing: 0.5px;
       margin-bottom: 4px;
       display: flex;
       align-items: center;
     }
     .edge-ai-metrics {
       font-size: 9px;
       line-height: 1.3;
       opacity: 0.9;
     }
 
     /* Safety Overlay */
     .edge-ai-overlay-safety {
       position: absolute;
       z-index: 3;
       pointer-events: none;
       font-family: monospace;
       animation: floatOverlayInverse 4s ease-in-out infinite alternate;
     }
     .edge-ai-box-safety {
       border: 2px solid #3b82f6;
       background: rgba(59, 130, 246, 0.15);
       box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
       padding: 10px 14px;
       border-radius: 8px;
       color: #60a5fa;
       backdrop-filter: blur(4px);
     }
     .edge-ai-dot-safety {
       display: inline-block;
       width: 8px;
       height: 8px;
       background-color: #10b981;
       border-radius: 50%;
       margin-right: 6px;
       animation: flash 0.8s infinite alternate;
     }
     .edge-ai-header-safety {
       font-size: 10px;
       font-weight: bold;
       letter-spacing: 0.5px;
       margin-bottom: 4px;
       display: flex;
       align-items: center;
     }
     .edge-ai-metrics-safety {
       font-size: 9px;
       line-height: 1.3;
       opacity: 0.9;
     }
 
     @keyframes floatOverlay {
       from { transform: translateY(0); }
       to { transform: translateY(-10px); }
     }
     @keyframes floatOverlayInverse {
       from { transform: translateY(0); }
       to { transform: translateY(10px); }
     }
     @keyframes flash {
       from { opacity: 0.3; }
       to { opacity: 1; }
     }
     
     .mobile-hero-bg-vid {
       position: absolute;
       inset: 0;
       width: 100%;
       height: 100%;
       z-index: 0;
       overflow: hidden;
       display: none !important;
     }

     @media (max-width: 991px) {
       .desktop-only {
         display: none !important;
       }
       .desktop-hero-bg-vid {
         display: none !important;
       }
       .mobile-hero-bg-vid {
         display: block !important;
       }
     }
   `}} />
  </div>
  
  {/* Mobile Video background loops with smooth cross-fade */}
  <div className="mobile-hero-bg-vid w-embed" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, overflow: "hidden" }}>
   <div className="hero-video-bg-mobile" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
    
    {/* Industrial Safety Iframe portrait */}
    {INDUSTRIAL_FEEDS.map((feed, idx) => {
      const isActive = activeVertical === "industrial" && activeIndFeed === idx;
      const shouldLoad = isMounted && isMobile && (activeVertical === "industrial" || loadSecondary);
      if (!shouldLoad && !isActive) return null;
      return (
        <div key={feed.id} style={{ opacity: isActive ? 1 : 0, zIndex: isActive ? 2 : 1, transition: "opacity 0.8s ease-in-out", position: "absolute", inset: 0 }}>
          {shouldLoad && feed.type === 'vimeo' ? (
             <iframe 
                allow="autoPlay; fullscreen; picture-in-picture" 
                allowFullScreen 
                frameBorder="0" 
                src={feed.srcMobile}
                style={{ position: "absolute", top: "42%", left: "50%", width: "100vw", height: "177.77vw", minHeight: "100vh", transform: "translate(-50%, -50%)", pointerEvents: "none", filter: "brightness(0.75) contrast(1.05) saturate(0.95)" }}
              />
          ) : shouldLoad && feed.type === 'local' ? (
             <video autoPlay loop muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.75) contrast(1.05)", position: "absolute", inset: 0 }}>
               <source src={feed.srcMobile.replace('.mp4', '.webm')} type="video/webm" />
               <source src={feed.srcMobile} type="video/mp4" />
             </video>
          ) : null}
        </div>
      );
    })}

    {/* Smart Agriculture swine crop loop portrait */}
    {AGRICULTURE_FEEDS.map((feed, idx) => {
      const isActive = activeVertical === "agriculture" && activeAgFeed === idx;
      const shouldLoad = isMounted && isMobile && (activeVertical === "agriculture" || loadSecondary);
      if (!shouldLoad && !isActive) return null;
      return (
        <div key={feed.id} style={{ opacity: isActive ? 1 : 0, zIndex: isActive ? 2 : 1, transition: "opacity 0.8s ease-in-out", position: "absolute", inset: 0 }}>
          {shouldLoad && (
             <video autoPlay loop muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.75) contrast(1.05)", position: "absolute", inset: 0 }}>
               <source src={feed.srcMobile.replace('.mp4', '.webm')} type="video/webm" />
               <source src={feed.srcMobile} type="video/mp4" />
             </video>
          )}
        </div>
      );
    })}
    
   </div>
   <style dangerouslySetInnerHTML={{__html: `
     .hero-video-bg-mobile {
       position: absolute;
       inset: 0;
       overflow: hidden;
       z-index: 0;
     }
   `}} />
  </div>

   {/* Telemetry Overlay / Watermark Cover */}
   <div className="telemetry-overlay" style={{ 
     position: 'absolute', 
     bottom: '85px', 
     right: '32px', 
     zIndex: 50, 
     display: 'flex', 
     flexDirection: 'column', 
     alignItems: 'flex-start', 
     justifyContent: 'center',
     gap: '10px', 
     background: 'rgba(9, 9, 11, 0.90)', 
     backdropFilter: 'blur(12px)',
     padding: '40px 40px', 
     borderRadius: '16px',
     border: '1px solid rgba(255,255,255,0.1)',
     boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
     minWidth: '280px',
     pointerEvents: 'none'
   }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: activeVertical === 'industrial' ? '#3b82f6' : '#10b981', boxShadow: `0 0 8px ${activeVertical === 'industrial' ? '#3b82f6' : '#10b981'}` }}></div>
        <span style={{ fontSize: '10px', color: '#f8fafc', fontFamily: 'monospace', letterSpacing: '1px' }}>{lang === 'vn' ? 'HỆ THỐNG BIÊN: ONLINE' : 'EDGE NODE: ONLINE'}</span>
      </div>
      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', alignSelf: 'flex-start', marginLeft: '14px' }}>
        {lang === 'vn' ? 'ĐỘ TRỄ: 12ms' : 'LATENCY: 12ms'}
      </div>
      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', alignSelf: 'flex-start', marginLeft: '14px' }}>
        {lang === 'vn' ? 'MÃ HÓA: AES-256' : 'ENCRYPTION: AES-256'}
      </div>
   </div>
</div>

    </>
  );
}
